import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { getServerPlayers, sendRcon } from "../http/ServerAPI";
import { useAsyncData } from "../hooks/useAsyncData";
import { useFilteredPlayers } from "../hooks/useFilteredPlayers";
import { observer } from "mobx-react-lite";
import BreadCrumbs from "../components/Breadcrumbs";
import { SERVERS_ROUTE } from "../utils/consts";
import { getApiErrorMessage } from "../http/apiError";
import PlayersToolbar from "./players/components/PlayersToolbar";
import PlayersTable from "./players/components/PlayersTable";
import { BalanceModal, ConfirmModal, VipModal } from "./players/components/PlayerModals";

const LIMIT = 50;

const PlayersPage = observer(() => {
    const { serverId } = useParams();
    const { data: players = [], loading } = useAsyncData(() => getServerPlayers(serverId), {
        deps: [serverId],
        initialData: [],
        errorMessage: 'Ошибка загрузки игроков',
    });
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("updated_at");
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [vipModalOpen, setVipModalOpen] = useState(false);
    const [balanceModalOpen, setBalanceModalOpen] = useState(false);
    const [kickModalOpen, setKickModalOpen] = useState(false);
    const [banModalOpen, setBanModalOpen] = useState(false);
    const [showAll, setShowAll] = useState(false);

    const hrefs = [{ href: SERVERS_ROUTE, name: "Список серверов" }];

    const filtered = useFilteredPlayers(players, search, sortBy);

    useEffect(() => {
        setShowAll(false);
    }, [search, sortBy, players]);

    const openModal = (type, player) => {
        setSelectedPlayer(player);
        if (type === "vip") setVipModalOpen(true);
        if (type === "balance") setBalanceModalOpen(true);
        if (type === "kick") setKickModalOpen(true);
        if (type === "ban") setBanModalOpen(true);
    };

    const closePlayerModals = () => {
        setVipModalOpen(false);
        setBalanceModalOpen(false);
        setKickModalOpen(false);
        setBanModalOpen(false);
    };

    const handleKick = async () => {
        if (!selectedPlayer) return;
        await sendRcon(serverId, `kick ${selectedPlayer.steam_id}`, 'command');
        setKickModalOpen(false);
    };

    const handleBan = async () => {
        if (!selectedPlayer) return;
        await sendRcon(serverId, `banid ${selectedPlayer.steam_id}`, 'command');
        setBanModalOpen(false);
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4 }}>
            <Box sx={{ width: '100%', marginTop: 3 }}>
                <BreadCrumbs hrefs={hrefs} name="Список игроков сервера" />
            </Box>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                <Typography variant="h4" gutterBottom mt={3}>Список игроков сервера</Typography>

                <PlayersToolbar search={search} onSearchChange={setSearch} sortBy={sortBy} onSortChange={setSortBy} />

                {loading ? (
                    <Box display="flex" justifyContent="center" mt={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <PlayersTable
                        players={filtered}
                        showAll={showAll}
                        limit={LIMIT}
                        onToggleShowAll={() => setShowAll(prev => !prev)}
                        onOpenModal={openModal}
                    />
                )}
            </motion.div>

            {selectedPlayer && (
                <>
                    <VipModal open={vipModalOpen} onClose={closePlayerModals} player={selectedPlayer} serverId={serverId} />
                    <BalanceModal open={balanceModalOpen} onClose={closePlayerModals} player={selectedPlayer} />
                </>
            )}

            <ConfirmModal open={kickModalOpen} onClose={() => setKickModalOpen(false)} title="Кикнуть игрока?" onConfirm={handleKick} />
            <ConfirmModal open={banModalOpen} onClose={() => setBanModalOpen(false)} title="Забанить игрока?" danger onConfirm={handleBan} />
        </Container>
    );
});

export default PlayersPage;
